import Cookies from 'js-cookie';
import { makeAutoObservable } from 'mobx';
import { DocumentContext } from 'next/document';
import cookie from 'cookie';
import { colors } from './colors';
import { Theme } from './types';
import { log } from 'utils/log';
import { stores } from 'state/stores';

export type ThemeType = 'dark' | 'light';

export const MIN_WIDTH_FOR_SIDEBAR_NAVIGATION = 1024;

export const isThemeType = (x: string): x is ThemeType => {
  return ['dark', 'light'].includes(x);
};

const light: Theme = {
  name: 'light',
  primary: colors.blackLight,
  secondary: colors.white,
  accent: colors.primary,
  background: colors.greyHiperLight,
  border: colors.greyLight,
};

const dark: Theme = {
  name: 'dark',
  primary: colors.white,
  secondary: colors.greyHiperDark,
  accent: colors.primary,
  background: colors.black,
  border: colors.greyDark,
};

const themes = {
  light,
  dark,
};

export class ThemeState {
  private themeKey: ThemeType = ((): ThemeType => {
    if (!process.browser) return 'light';

    return Cookies.get('theme') === 'dark' ? 'dark' : 'light';
  })();
  private savedKey?: ThemeType;

  constructor() {
    makeAutoObservable(this);
  }

  updateThemeWithDocumentContext = (context: DocumentContext) => {
    const rawCookies = context.req?.headers.cookie;
    const cookies = rawCookies ? cookie.parse(rawCookies) : {};
    const themeCookie = cookies['theme'] as 'dark' | 'light';

    log('🍪 themeCookie: ', themeCookie);

    this.themeKey = ['dark', 'light'].includes(themeCookie) ? themeCookie : 'light';
  };

  get current() {
    // always display light theme if not logged in
    if (!stores.account.username) {
      return themes['light'];
    }

    return themes[this.themeKey];
  }

  onChangeTheme = (newThemeKey: ThemeType) => {
    this.themeKey = newThemeKey;
    Cookies.set('theme', newThemeKey);
  };

  /**
   * saves the current theme in memory. used to force light theme
   * during onboarding and restoring the previous one when done
   */
  push = (newThemeKey: ThemeType) => {
    this.savedKey = this.themeKey;
    this.onChangeTheme(newThemeKey);
  };

  pop = () => {
    if (!this.savedKey) return;
    this.onChangeTheme(this.savedKey);
    this.savedKey = undefined;
  };
}

export const themeState = new ThemeState();
